import isEmpty from 'lodash/isEmpty';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {focusCurrentList} from '../../../common/focusCurrentList.function';
import {t} from '../../../common/getTranslations';
import {Divider} from '../../../design-system/atoms/Divider/Divider';
import {InfinityScroll} from '../../../design-system/atoms/InfinityScroll/InfinityScroll';
import {FOOTER_HEIGHT} from '../../../design-system/organisms/Footer/FooterHeight.constant';
import {HeaderTitle} from '../../../design-system/organisms/HeaderTitle/HeaderTitle';
import {MoreExpandList} from '../../../design-system/organisms/MoreExpandList/MoreExpandList';
import {Pagination} from '../../../design-system/organisms/Pagination/Pagination';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../header/constants';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {theme} from '../../../theme';
import {HELP_NUM_PER_PAGE} from '../constant';
import {HelpItem} from '../type';

interface HelpProps {
  helpData?: HelpItem[];
  helpId: string;
  helpPage: number;
  totalCount: number;
  isWebView: boolean;
  isMobile: boolean;
  onClickHelp: (e: React.SyntheticEvent<HTMLElement, Event>) => void;
  onChangePage: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

export function Help({
  helpData,
  helpId,
  helpPage,
  totalCount,
  isMobile,
  isWebView,
  onClickHelp,
  onChangePage,
}: HelpProps) {
  const currentHelpElement = useRef<HTMLElement>(null);
  const [scrollPage, setScrollPage] = useState<number>(helpPage ?? 1);

  const loadNextPage = useCallback(() => {
    if (helpData) {
      setScrollPage(currentScrollPage => {
        if (currentScrollPage * HELP_NUM_PER_PAGE < totalCount) {
          return currentScrollPage + 1;
        }
        return currentScrollPage;
      });
    }
  }, [helpData, totalCount]);

  useEffect(() => {
    if (isMobile) {
      focusCurrentList({listElement: currentHelpElement, isWebView});
    }
  }, [currentHelpElement, isMobile, helpId, helpPage, isWebView]);

  const HelpComponent = useMemo(() => {
    if (!helpData) {
      return null;
    }

    const startIndex = isMobile ? 0 : (helpPage - 1) * HELP_NUM_PER_PAGE;
    const endIndex = isMobile
      ? scrollPage * HELP_NUM_PER_PAGE
      : startIndex + HELP_NUM_PER_PAGE;

    const slicedHelpData = helpData.slice(startIndex, endIndex);
    return slicedHelpData.map((item, index) => {
      const {id, title, contents} = item;
      const isActive = helpId === item.id;
      return (
        <article
          key={id}
          {...putTiaraObjectProps({
            click: {
              ordnum: String(index + 1),
              copy: isActive ? '접기' : '펼치기',
            },
          })}
        >
          <MoreExpandList
            ref={isActive ? currentHelpElement : null}
            isActive={isActive}
            id={id}
            title={title}
            contents={contents}
            onClick={onClickHelp}
          />
          <Divider color={theme.colors['theme-transparent-10']} />
        </article>
      );
    });
  }, [helpData, helpId, helpPage, isMobile, onClickHelp, scrollPage]);

  return (
    <div
      className="font-small1 w-full pb-50pxr leading-[21px] text-theme-solid-100"
      {...putTiaraObjectProps({
        click: {layer1: '도움말'},
      })}
    >
      {!isMobile && (
        <HeaderTitle className="mt-20pxr mb-32pxr">
          {t('more_customer_help')}
        </HeaderTitle>
      )}
      {helpData &&
        (isMobile ? (
          <InfinityScroll
            canLoadNextPage={helpPage < totalCount}
            requestNextPageData={loadNextPage}
          >
            <dl>{HelpComponent}</dl>
          </InfinityScroll>
        ) : (
          <>
            <dl>{HelpComponent}</dl>
            <Pagination
              currentPage={helpPage}
              onChangePage={onChangePage}
              totalCount={totalCount}
            />
          </>
        ))}
      {isEmpty(helpData) && (
        <div
          // 관련 디자인가이드가 없음. 일단 대충 아무색상으로나 넣어놓았다.
          className="font-medium1 flex items-center justify-center bg-theme-bg-surface text-theme-solid-100 [&_p]:m-0"
          style={{
            minHeight: `calc(100vh - ${MOBILE_HEADER_HEIGHT_PX}px - ${FOOTER_HEIGHT}px)`,
          }}
        >
          <p>{t('more_customer_help_empty')}</p>
        </div>
      )}
    </div>
  );
}
